(() => {
  'use strict';

  const hydrate = (image) => {
    image.parentElement?.querySelectorAll('source[data-v4-lazy-srcset]').forEach((source) => {
      source.srcset = source.dataset.v4LazySrcset;
      delete source.dataset.v4LazySrcset;
    });
    if (image.dataset.v4LazySrcset) image.srcset = image.dataset.v4LazySrcset;
    if (image.dataset.v4LazySrc) image.src = image.dataset.v4LazySrc;
    delete image.dataset.v4LazySrc;
    delete image.dataset.v4LazySrcset;
  };

  const hydrateAfterLcp = () => {
    document.querySelectorAll('img[data-v4-after-lcp-src]').forEach((image) => {
      if (image.dataset.v4AfterLcpSrcset) image.srcset = image.dataset.v4AfterLcpSrcset;
      image.src = image.dataset.v4AfterLcpSrc;
      delete image.dataset.v4AfterLcpSrc;
      delete image.dataset.v4AfterLcpSrcset;
    });
  };

  const scheduleAfterLcp = () => {
    const priorityImage = document.querySelector('.main-splide .is-v4-priority img');
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(hydrateAfterLcp, { timeout: 1200 });
      } else {
        setTimeout(hydrateAfterLcp, 0);
      }
    };
    if (!priorityImage || priorityImage.complete) schedule();
    else priorityImage.addEventListener('load', schedule, { once: true });
  };

  const observe = () => {
    const images = [...document.querySelectorAll('img[data-v4-lazy-src]')];
    if (!('IntersectionObserver' in window)) return images.forEach(hydrate);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        hydrate(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '200px 0px' });
    images.forEach((image) => observer.observe(image));
  };

  const loaderScript = document.currentScript;
  const glightboxSrc = loaderScript?.dataset.glightboxSrc;
  const glightboxCssSrc = loaderScript?.dataset.glightboxCssSrc;
  const sectionsSrc = loaderScript?.dataset.sectionsSrc;
  const searchSrc = loaderScript?.dataset.searchSrc;
  const scriptPromises = new Map();
  const loadScript = (key, src, ready) => {
    if (ready?.()) return Promise.resolve();
    if (scriptPromises.has(key)) return scriptPromises.get(key);
    const promise = new Promise((resolve, reject) => {
      if (!src) return resolve();
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    scriptPromises.set(key, promise);
    return promise;
  };

  const stylePromises = new Map();
  const loadStyle = (key, href) => {
    if (stylePromises.has(key)) return stylePromises.get(key);
    const promise = new Promise((resolve, reject) => {
      if (!href) return resolve();
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
    stylePromises.set(key, promise);
    return promise;
  };

  const loadGlightbox = () => Promise.all([
    loadStyle('glightbox', glightboxCssSrc),
    loadScript('glightbox', glightboxSrc, () => Boolean(window.GLightbox)),
  ]);

  const bindGlightbox = () => {
    if (!glightboxSrc) return;
    document.addEventListener('click', async (event) => {
      const link = event.target.closest('a.glightbox');
      if (!link || window.GLightbox) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      await loadGlightbox();
      window.GLightbox({ selector: '.glightbox', videosWidth: '1290px', loop: true });
      link.click();
    }, { capture: true });
  };

  const bindDeferredThemeModules = () => {
    const loadSections = () => loadScript('sections', sectionsSrc);
    const loadSearch = () => loadScript('search', searchSrc);
    const sectionsCapabilitySelector = [
      '.sticky-announcement-bar', '.marquee', '.recommended-products', '.featured-products',
      '.recently-viewed', '.testimonials', '.instagram-gallery', '.tiktok-slider',
      '.parallax-image', '#newsletter-popup-modal', '.image-compare-slider',
      '.animated-stories-link', '.quiz-header', '.animated-counter', '.scrolling',
      '.sticky-video', '.discount-bar'
    ].join(',');
    if (document.querySelector(sectionsCapabilitySelector)) {
      window.addEventListener('scroll', () => loadSections().catch(() => {}), { once: true, passive: true });
      window.addEventListener('load', () => {
        setTimeout(() => loadSections().catch(() => {}), 2500);
      }, { once: true });
    }
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.matches?.(sectionsCapabilitySelector) || event.target.querySelector?.(sectionsCapabilitySelector)) {
        loadSections().catch(() => {});
      }
    });
    document.addEventListener('pointerdown', (event) => {
      if (event.target.closest('#offcanvas-search, [data-bs-target="#offcanvas-search"], [href="#offcanvas-search"]')) {
        loadSearch().catch(() => {});
      }
    }, { capture: true, passive: true });
    document.addEventListener('focusin', (event) => {
      if (event.target.closest('#offcanvas-search, [data-bs-target="#offcanvas-search"], [href="#offcanvas-search"]')) {
        loadSearch().catch(() => {});
      }
    }, { capture: true });
  };

  const init = () => {
    observe();
    scheduleAfterLcp();
    bindGlightbox();
    bindDeferredThemeModules();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
